// addNamedWindow — VQE-Edit edit-action: add a `WINDOW w AS (…)` definition via
// SR-Edit's `add-named-window` op. SR-Edit synthesizes the leading `WINDOW`
// keyword (at the correct clause position) when the statement has no WINDOW
// clause yet; otherwise the new definition is appended as a comma-separated peer.
//
// `bodyText` is the OVER body WITHOUT the surrounding parens — SR-Edit wraps it.
//
// Task: VQE Polish §4b-ii — named-window card real editing.

import { commitEdit, type CommitEditCtx } from '../commitEdit';

const IDENT_PATTERN = /^[A-Za-z_][A-Za-z0-9_$]*$|^"[^"]*"$|^`[^`]*`$|^\[[^\]]*\]$/;

export interface AddNamedWindowOptions {
  /** Insert AFTER this 0-based index in the existing definition list. Default: append. */
  readonly afterIndex?: number;
}

export async function addNamedWindow(
  name: string,
  bodyText: string,
  ctx: CommitEditCtx,
  options: AddNamedWindowOptions = {},
): Promise<void> {
  const trimmedName = name.trim();
  if (trimmedName.length === 0) throw new Error('addNamedWindow: name must not be empty');
  if (!IDENT_PATTERN.test(trimmedName)) {
    throw new Error(
      `addNamedWindow: name "${trimmedName}" is not a valid identifier (use bare letters/digits/underscores or a quoted form)`,
    );
  }
  // The body may legitimately be empty (`WINDOW w AS ()` is valid SQL — an
  // unbounded window), so no non-empty guard here.
  await commitEdit(
    {
      op: 'add-named-window',
      name: trimmedName,
      bodyText: bodyText.trim(),
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}
